/**
 * @file PublicFooter.jsx
 * @description Shared footer for public (unauthenticated) pages. Grouped into
 *   4 short columns (For Providers / For Agencies / Resources / Company)
 *   rather than a flat link dump — a handful of the most useful routes per
 *   audience, not every public SEO URL. Manages the Contact modal's open
 *   state internally so any public page can drop it in without extra wiring.
 *   Dark navy/indigo gradient — matches the hero header used across every
 *   public SEO page (CityRolePage, AboutAbaCases, the Phase 2/3 family), so
 *   the footer reads as the same product instead of a bolted-on light strip.
 * @author Victor Carpinelli
 * @version 3.0.0
 * @module components/PublicFooter
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import ContactModal from "./ContactModal";
import { SocialIcon } from "./SocialIcons";
import { track, A, ctaMeta } from "../lib/analytics";
import { SOCIAL_LINKS, FACEBOOK_GROUP } from "../lib/seoSchema";
import i18n from "../i18n";

const SUPPORT_EMAIL = "support@abacases.com";
const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO_LT = "#818cf8";
const TEXT_ON_DARK = "rgba(255,255,255,0.72)";
const BORDER_ON_DARK = "rgba(255,255,255,0.12)";

const COLUMNS = [
  {
    titleKey: "publicFooter.forProvidersTitle",
    audience: "provider",
    links: [
      { href: "/aba-case-finder/", labelKey: "publicFooter.findAbaCases" },
      { href: "/aba-provider-waitlist/", labelKey: "publicFooter.joinCaseAlerts" },
      { href: "/rbt-jobs-florida/", labelKey: "publicFooter.rbtJobsFlorida" },
      // One link to every location (lib/seoLocations): the footer never lists cities, however many are released.
      { href: "/aba-jobs-by-state/", labelKey: "publicFooter.abaJobsByState" },
      { href: "/free-aba-timesheet-payment-tracker/", labelKey: "publicFooter.freeAbaTracker" },
      { href: FACEBOOK_GROUP.url, labelKey: "publicFooter.facebookGroup", external: true },
    ],
  },
  {
    titleKey: "publicFooter.forAgenciesTitle",
    audience: "agency",
    links: [
      { href: "/aba-agencies/", labelKey: "publicFooter.forAgencies" },
      { href: "/post-aba-cases/", labelKey: "publicFooter.postAbaCases" },
      { href: "/aba-staffing-for-small-agencies/", labelKey: "publicFooter.abaStaffingSmallAgencies" },
      { href: "/aba-staffing-florida/", labelKey: "publicFooter.abaStaffingFlorida" },
      { href: "/rbt-staffing-miami-fl/", labelKey: "publicFooter.rbtStaffingMiami" },
      { href: "/rbt-staffing-charlotte-nc/", labelKey: "publicFooter.rbtStaffingCharlotte" },
    ],
  },
  {
    titleKey: "publicFooter.resourcesTitle",
    audience: "resource",
    links: [
      { href: "/aba-resources/", labelKey: "publicFooter.abaResources" },
      { href: "/how-to-become-an-rbt/", labelKey: "publicFooter.howToBecomeRbt" },
      { href: "/how-to-become-a-bcba/", labelKey: "publicFooter.howToBecomeBcba" },
      { href: "/how-to-become-a-bcaba/", labelKey: "publicFooter.howToBecomeBcaba" },
      { href: "/aba-roles-guide/", labelKey: "publicFooter.abaRolesExplained" },
    ],
  },
];

/**
 * Outbound links to the official brand profiles. These are the reciprocal half
 * of the Organization `sameAs` in seoSchema.js — a one-directional claim in
 * JSON-LD carries far less weight than one the site itself also points at.
 */
function SocialRow() {
  const { t } = useTranslation();
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {SOCIAL_LINKS.map((s) => (
        <a
          key={s.name}
          href={s.url}
          target="_blank"
          rel="me noopener noreferrer"
          aria-label={t('publicFooter.followOn', { network: s.name })}
          title={t('publicFooter.followOn', { network: s.name })}
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 44, height: 44, borderRadius: 8,
            border: `1px solid ${BORDER_ON_DARK}`,
            color: TEXT_ON_DARK, transition: "color 0.15s, border-color 0.15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = INDIGO_LT; e.currentTarget.style.borderColor = INDIGO_LT; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = TEXT_ON_DARK; e.currentTarget.style.borderColor = BORDER_ON_DARK; }}
          onClick={() => {
            track(A.FOOTER_LINK_CLICK, {
              supabaseMeta: { href: s.url },
              ga4Params: ctaMeta({
                pagePath: window.location.pathname,
                pageType: "footer",
                ctaLabel: s.name,
                targetPath: s.url,
                userLanguage: i18n.language,
                audience: "company",
              }),
            });
          }}
        >
          <SocialIcon name={s.name} />
        </a>
      ))}
    </div>
  );
}

/** Link/button that brightens to indigo-light on hover — cheap polish, no extra deps. */
function FooterLink({ href, onClick, columnAudience, external, children }) {
  const style = {
    color: TEXT_ON_DARK,
    fontSize: 14,
    fontWeight: 500,
    textDecoration: "none",
    transition: "color 0.15s",
    width: "fit-content",
    maxWidth: "100%",
    // 44px row via padding/min-height (not gap) so the whole row is tappable.
    display: "flex",
    alignItems: "center",
    minHeight: 44,
    overflowWrap: "anywhere",
  };
  const handlers = {
    onMouseEnter: (e) => { e.currentTarget.style.color = INDIGO_LT; },
    onMouseLeave: (e) => { e.currentTarget.style.color = TEXT_ON_DARK; },
  };
  if (onClick) {
    return (
      <button type="button" onClick={onClick} style={{ ...style, background: "none", border: "none", padding: 0, margin: 0, cursor: "pointer", textAlign: "left", fontFamily: T.fontBody }} {...handlers}>
        {children}
      </button>
    );
  }
  return (
    <a
      href={href}
      style={style}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      onClick={() => {
        track(A.FOOTER_LINK_CLICK, {
          supabaseMeta: { href },
          ga4Params: ctaMeta({
            pagePath: window.location.pathname,
            pageType: "footer",
            ctaLabel: typeof children === "string" ? children : href,
            targetPath: href,
            userLanguage: i18n.language,
            audience: columnAudience,
          }),
        });
      }}
      {...handlers}
    >
      {children}
    </a>
  );
}

export default function PublicFooter() {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <>
      <footer style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: "40px 24px 22px", fontFamily: T.fontBody, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", bottom: -100, right: "8%", width: 360, height: 360, borderRadius: "50%", background: "rgba(79,70,229,0.14)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: 1080, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 28, marginBottom: 28 }}>
            {COLUMNS.map((col) => (
              <div key={col.titleKey}>
                <div style={{ fontSize: 12, fontWeight: 800, color: INDIGO_LT, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
                  {t(col.titleKey)}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  {col.links.map((l) => (
                    <FooterLink key={l.href} href={l.href} columnAudience={col.audience} external={l.external}>{t(l.labelKey)}</FooterLink>
                  ))}
                </div>
              </div>
            ))}
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: INDIGO_LT, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
                {t('publicFooter.companyTitle')}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                <FooterLink href="/about-aba-cases/" columnAudience="company">{t('publicFooter.aboutAbaCases')}</FooterLink>
                <FooterLink href="/terms-of-use" columnAudience="company">{t('publicFooter.termsOfUse')}</FooterLink>
                <FooterLink href="/privacy-policy" columnAudience="company">{t('publicFooter.privacyPolicy')}</FooterLink>
                <FooterLink
                  columnAudience="company"
                  onClick={() => {
                    track(A.FOOTER_LINK_CLICK, {
                      supabaseMeta: { href: "contact-modal" },
                      ga4Params: ctaMeta({
                        pagePath: window.location.pathname,
                        pageType: "footer",
                        ctaLabel: "Contact",
                        targetPath: "contact-modal",
                        userLanguage: i18n.language,
                        audience: "company",
                      }),
                    });
                    setOpen(true);
                  }}
                >
                  {t('publicFooter.contact')}
                </FooterLink>
                <FooterLink href={`mailto:${SUPPORT_EMAIL}`} columnAudience="company">{SUPPORT_EMAIL}</FooterLink>
              </div>
            </div>
          </div>
          <div style={{ borderTop: `1px solid ${BORDER_ON_DARK}`, paddingTop: 16, display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}>
              © {new Date().getFullYear()} {t('publicFooter.copyright')}
            </span>
            <SocialRow />
          </div>
        </div>
      </footer>

      {open && <ContactModal onClose={() => setOpen(false)} />}
    </>
  );
}
